// O bloco de texto que o cobrador cola no WhatsApp antes de sair para a rua.
//
// O formato foi especificado pelo gestor, ao pé da letra:
//
//   1. {UC} - {Titular}
//   {Nome Associado} - {Contato}
//   {Qtd Faturas Vencidas} - {Valor}
//
//   {Link boletos da Equatorial}
//   {Link Boletos da Vigor}
//   {Link localização do google maps}
//   {Endereço Escrito}
//   {Link preenchimento rápido de parcelamento}
//
// Mais o resumo das tratativas, que ele pediu depois: "foi tentado tratar assim,
// entrar em contato, já foi negociado mas não foi pago".
//
// A mensagem é para QUEM COBRA, não para o cliente — é a folha de rota dele. Por
// isso os links podem ser do sistema (autenticados), e nenhum dado de cliente
// precisa ir para uma página pública.

/** Uma parada, do jeito que ela aparece na mensagem. */
export interface ParadaMensagem {
    posicao: number;
    ucCodigo: string;
    /** Quem assina a fatura na Equatorial — some da linha quando não há. */
    titular?: string | null;
    clienteNome: string;
    /** Só dígitos; o primeiro é o que aparece. */
    telefones?: string[];
    qtdFaturas: number;
    valorTotal: number;
    /** 'yyyy-mm-dd'. */
    vencMaisAntigo?: string | null;
    enderecoTexto?: string | null;
    /** PDF da fatura da distribuidora. */
    pdfFatura?: string | null;
    /** Link de navegação já resolvido por `urlNavegacaoUc`. */
    urlMaps?: string | null;
    /** Termo de adesão resolvido por `termosPorUc` — o contrato que encerra o
     *  "eu nunca assinei". Ausente na maioria: só 15% das UCs vencidas tem. */
    termo?: { url: string; dataAssinatura: string | null } | null;
    /** Faturas a parcelar, para o deep-link de parcelamento. */
    faturaIds?: Array<string | number>;
    /**
     * Outras UCs do mesmo cliente cobradas nesta porta (16/09/2026). Com elas,
     * `qtdFaturas` e `valorTotal` são o TOTAL da parada — é o que se cobra ali.
     */
    ucsExtras?: Array<{ ucCodigo: string; qtdFaturas: number; valorTotal: number }>;
    /** Evento mais recente de `timeline_cobranca_cliente`. */
    ultimaTratativa?: {
        em?: string | null;
        titulo?: string | null;
        detalhe?: string | null;
        autor?: string | null;
    } | null;
}

export interface OpcoesMensagem {
    /** Origem do app, sem barra no fim. Ex.: 'https://vigor-hub.vercel.app'. */
    baseUrl: string;
    /** Rota já gravada. Sem ela, a linha de boletos não é montada. */
    rotaId?: string | null;
    /**
     * Versão curta: sai o endereço em texto, o link do Maps, o de parcelamento, o
     * do termo e a tratativa — tudo isso já está na tela da parada, que o link
     * "Boletos no VigorHub" abre direto.
     *
     * Existe porque o formato cheio tem ~450 caracteres por parada: sete paradas
     * passam de 3.000 e o link `wa.me/?text=` não entrega mensagem desse tamanho
     * (ver LIMITE_WHATSAPP_LINK). O formato cheio continua sendo o padrão — é o
     * que o gestor especificou — e este só entra quando não cabe.
     */
    compacta?: boolean;
}

/**
 * Acima disto o `wa.me/?text=` não entrega a mensagem.
 *
 * O sintoma que o gestor relatou: rota com muitos endereços "não aparece" no
 * WhatsApp. A causa é o tamanho da URL — `window.open('https://wa.me/?text=' +
 * encodeURIComponent(texto))`. O encode triplica o que é acento, quebra de linha
 * e `:` de URL, então 1.800 caracteres de texto já viram mais de 4.000 na URL, e
 * daí para cima o WhatsApp abre em branco ou corta no meio.
 *
 * O número é conservador de propósito: perder o fim da folha de rota sem aviso é
 * muito pior do que pedir para colar.
 */
export const LIMITE_WHATSAPP_LINK = 1800;

/** A mensagem cabe no link direto, ou tem que ser copiada e colada? */
export const cabeNoLinkWhatsapp = (texto: string): boolean =>
    String(texto || '').length <= LIMITE_WHATSAPP_LINK;

/**
 * Qual das duas versões mandar.
 *
 * A curta SÓ se paga quando ela cabe no link e a cheia não — aí ela converte um
 * envio quebrado num envio que funciona. Quando nem a curta cabe (rota grande, e
 * é o caso a partir de umas 6 paradas), encurtar não resolve nada: o envio vai
 * pela área de transferência de qualquer jeito, que não tem limite. Aí a
 * mensagem vai COMPLETA, no formato que o gestor especificou.
 *
 * Perder o endereço, o mapa e o histórico de graça seria o pior dos dois mundos.
 */
export const escolherFormatoMensagem = (
    cheia: string,
    curta: string,
): 'cheia' | 'curta' =>
    (!cabeNoLinkWhatsapp(cheia) && cabeNoLinkWhatsapp(curta)) ? 'curta' : 'cheia';

const semBarra = (url: string): string => String(url || '').replace(/\/+$/, '');

const moeda = (valor: number): string =>
    (Number(valor) || 0)
        .toLocaleString('pt-BR', {
            style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2,
        })
        // O Intl separa "R$" do numero com espaco NAO-QUEBRAVEL (U+00A0). Ele
        // some na tela, mas o texto vai ser copiado, colado no WhatsApp e as
        // vezes conferido contra a planilha — caractere invisivel ali vira
        // divergencia que ninguem consegue enxergar.
        .replace(/\u00a0/g, ' ');

/**
 * 'yyyy-mm-dd' -> 'dd/MM/yyyy', sem passar por Date.
 *
 * `new Date('2026-03-12')` é interpretado como UTC e, em fuso negativo, volta um
 * dia — o export de faturas já saiu com data a menos por causa disso. Aqui é
 * corte de string, não tem como errar.
 */
export const dataBr = (iso: string | null | undefined): string => {
    const v = String(iso || '').split('T')[0];
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/** 'dd/MM' — a tratativa não precisa do ano, e a linha fica curta. */
const diaEMes = (iso: string | null | undefined): string => {
    const completo = dataBr(iso);
    return completo ? completo.slice(0, 5) : '';
};

/** (62) 99259-5552, ou o valor cru quando o número foge do padrão. */
export const telefoneBr = (valor: string | null | undefined): string => {
    const d = String(valor || '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return String(valor || '').trim();
};

/** Deep-link da parada dentro da rota, onde ficam os boletos daquela UC. */
export const linkBoletosParada = (
    posicao: number,
    { baseUrl, rotaId }: OpcoesMensagem,
): string => (rotaId
    ? `${semBarra(baseUrl)}/#/financeiro/rota-cobranca?rota=${encodeURIComponent(rotaId)}&parada=${posicao}`
    : '');

/** Deep-link que abre o parcelamento com as faturas da UC já selecionadas. */
export const linkParcelamento = (
    faturaIds: Array<string | number> | undefined,
    { baseUrl }: OpcoesMensagem,
): string => {
    const ids = (faturaIds || []).map(id => String(id).trim()).filter(Boolean);
    if (ids.length === 0) return '';
    return `${semBarra(baseUrl)}/#/financeiro/parcelamentos?novo=1&faturas=${encodeURIComponent(ids.join(','))}`;
};

/** A linha do histórico: "Últ. tratativa: 02/09 — ligação, prometeu pagar (Daiane)". */
export const linhaTratativa = (t: ParadaMensagem['ultimaTratativa']): string => {
    if (!t) return '';
    const quando = diaEMes(t.em);
    const oque = [t.titulo, t.detalhe].map(v => String(v || '').trim()).filter(Boolean).join(' · ');
    if (!quando && !oque) return '';
    const autor = String(t.autor || '').trim();
    return `Últ. tratativa: ${[quando, oque].filter(Boolean).join(' — ')}${autor ? ` (${autor})` : ''}`;
};

/**
 * O bloco de uma parada.
 *
 * Toda linha opcional some quando não tem valor, em vez de sair vazia ou com
 * um link quebrado. Isso importa mais do que parece: só 45% das faturas
 * vencidas têm algum meio de pagamento gravado e 18% não têm o PDF da
 * distribuidora — um "Fatura Equatorial:" seguido de nada é pior que a ausência
 * da linha, porque manda o cobrador procurar o que não existe.
 */
export const blocoParada = (p: ParadaMensagem, opcoes: OpcoesMensagem): string => {
    const linhas: string[] = [];

    const titular = String(p.titular || '').trim();
    linhas.push(`${p.posicao}. ${p.ucCodigo}${titular ? ` — ${titular}` : ''}`);

    const telefone = telefoneBr((p.telefones || [])[0]);
    const cliente = String(p.clienteNome || '').trim();
    if (cliente || telefone) {
        linhas.push([cliente, telefone].filter(Boolean).join(' — '));
    }

    const plural = p.qtdFaturas === 1 ? 'fatura vencida' : 'faturas vencidas';
    const desde = dataBr(p.vencMaisAntigo);
    const extras = p.ucsExtras || [];
    const total = extras.length > 0 ? ` no total, ${extras.length + 1} UCs` : '';
    linhas.push(
        `${p.qtdFaturas} ${plural} — ${moeda(p.valorTotal)}${total}${desde ? ` (mais antiga: ${desde})` : ''}`,
    );
    // Uma linha por UC juntada: sem isso o cobrador cobra o total sem saber que
    // parte dele é de outra unidade — e é a primeira pergunta do cliente.
    extras.forEach(e => {
        const pl = e.qtdFaturas === 1 ? 'fatura' : 'faturas';
        linhas.push(`+ UC ${e.ucCodigo}: ${e.qtdFaturas} ${pl} — ${moeda(e.valorTotal)}`);
    });

    // Linha em branco separando o cabeçalho dos links, como no formato pedido.
    const detalhes: string[] = [];
    const pdf = String(p.pdfFatura || '').trim();
    if (pdf) detalhes.push(`Fatura Equatorial: ${pdf}`);

    const boletos = linkBoletosParada(p.posicao, opcoes);
    if (boletos) detalhes.push(`Boletos no VigorHub: ${boletos}`);

    // Na versão curta para aqui: o link acima abre a parada com endereço, mapa,
    // parcelamento, termo e histórico. Repetir tudo em texto é o que estoura o
    // tamanho da mensagem.
    if (opcoes.compacta) {
        if (detalhes.length > 0) linhas.push('', ...detalhes);
        return linhas.join('\n');
    }

    const maps = String(p.urlMaps || '').trim();
    if (maps) detalhes.push(`Maps: ${maps}`);

    const endereco = String(p.enderecoTexto || '').trim();
    if (endereco) detalhes.push(endereco);

    const parcelar = linkParcelamento(p.faturaIds, opcoes);
    if (parcelar) detalhes.push(`Parcelar: ${parcelar}`);

    // O contrato vem DEPOIS do parcelamento e antes da tratativa: quando o
    // cliente diz que nunca assinou, o cobrador ja esta com a proposta na mao e
    // so precisa provar a adesao. A data importa tanto quanto o PDF — e ela que
    // o cliente contesta.
    const termo = p.termo;
    if (termo?.url) {
        const quando = dataBr(termo.dataAssinatura);
        detalhes.push(quando ? `Termo assinado em ${quando}: ${termo.url}` : `Termo de adesão: ${termo.url}`);
    }

    const tratativa = linhaTratativa(p.ultimaTratativa);
    if (tratativa) detalhes.push(tratativa);

    if (detalhes.length > 0) linhas.push('', ...detalhes);

    return linhas.join('\n');
};

/**
 * A mensagem inteira: um bloco por parada, separados por linha em branco.
 *
 * As paradas saem na ordem em que chegam — a ordem da rota é decidida pelo
 * otimizador, não aqui.
 */
export const mensagemRota = (
    paradas: ParadaMensagem[],
    opcoes: OpcoesMensagem,
): string => (paradas || []).map(p => blocoParada(p, opcoes)).join('\n\n');

/** Cabeçalho opcional, para quando a mensagem vai num grupo. */
export const cabecalhoRota = (
    dados: { cidade?: string | null; data?: string | null; paradas: number; valorTotal: number },
): string => {
    const partes = [`Cobrança — ${dados.paradas} ${dados.paradas === 1 ? 'parada' : 'paradas'}`];
    const cidade = String(dados.cidade || '').trim();
    if (cidade) partes.push(cidade);
    const data = dataBr(dados.data);
    if (data) partes.push(data);
    return `${partes.join(' · ')}\nTotal em aberto: ${moeda(dados.valorTotal)}`;
};
